import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Minus, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, money } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { Reveal } from "@/components/Reveal";
import Stars from "@/components/Stars";

const inputCls =
  "w-full border border-white/15 bg-obsidian px-4 py-3 font-mono text-xs tracking-wider text-bone outline-none transition-colors placeholder:text-faint focus:border-vermilion";

function ReviewForm({ productId, onDone }) {
  const [form, setForm] = useState({ name: "", rating: 5, text: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/reviews", { product_id: productId, ...form, rating: Number(form.rating) });
      toast.success("Thank you — your words await the atelier's approval.");
      setForm({ name: "", rating: 5, text: "" });
      onDone();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={submit} className="flex flex-col gap-3 border border-white/10 bg-onyx p-6" data-testid="review-form">
      <p className="mb-1 font-mono text-xs uppercase tracking-[0.3em] text-smoke">Leave your words</p>
      <input required placeholder="YOUR NAME" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="review-name-input" className={inputCls} />
      <select value={form.rating} onChange={(e) => setForm({ ...form, rating: e.target.value })} data-testid="review-rating-select" className={inputCls}>
        {[5, 4, 3, 2, 1].map((r) => (
          <option key={r} value={r}>{r} — {"★".repeat(r)}</option>
        ))}
      </select>
      <textarea required rows={3} placeholder="HOW HAS IT AGED WITH YOU?" value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} data-testid="review-text-input" className={`${inputCls} resize-none`} />
      <button type="submit" disabled={sending} data-testid="review-submit-button" className="bg-vermilion py-3.5 font-mono text-xs uppercase tracking-[0.3em] text-obsidian transition-colors hover:bg-vermilion-dark disabled:opacity-40">
        {sending ? "Sealing…" : "Submit for Approval"}
      </button>
    </form>
  );
}

export default function ProductDetail() {
  const { id } = useParams();
  const { add } = useCart();
  const [product, setProduct] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [qty, setQty] = useState(1);
  const [notFound, setNotFound] = useState(false);

  const loadReviews = () => api.get(`/reviews?product_id=${id}`).then(({ data }) => setReviews(data)).catch(() => {});

  useEffect(() => {
    setProduct(null);
    setNotFound(false);
    api.get(`/products/${id}`).then(({ data }) => setProduct(data)).catch(() => setNotFound(true));
    loadReviews();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (notFound) {
    return <div className="flex min-h-screen items-center justify-center font-display text-3xl italic text-faint">This object has left the atelier.</div>;
  }
  if (!product) {
    return <div className="flex min-h-screen items-center justify-center font-mono text-xs uppercase tracking-[0.3em] text-faint">Retrieving the object…</div>;
  }

  const avg = reviews.length ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1) : null;

  return (
    <div className="px-6 pb-24 pt-36 sm:px-12 lg:px-20" data-testid="product-detail-page">
      <div className="grid gap-14 lg:grid-cols-12">
        <Reveal className="lg:col-span-6">
          <div className="group relative overflow-hidden border border-white/10">
            <motion.img
              src={product.image}
              alt={product.name}
              initial={{ scale: 1.08 }}
              animate={{ scale: 1 }}
              transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1] }}
              className="aspect-[4/5] w-full object-cover"
            />
            <div className="pointer-events-none absolute inset-0 shadow-[inset_0_0_100px_rgba(0,0,0,0.55)]" />
          </div>
        </Reveal>
        <div className="lg:col-span-5 lg:col-start-8">
          <Reveal delay={0.1}>
            <p className="eyebrow-label mb-4">{product.category}</p>
            <h1 className="font-display text-4xl font-light leading-tight text-bone sm:text-5xl">{product.name}</h1>
            <div className="mt-5 flex items-center gap-4">
              <span className="font-mono text-xl tracking-wider text-bone" data-testid="product-price">{money(product.price)}</span>
              {avg && (
                <span className="flex items-center gap-2">
                  <Stars rating={Math.round(avg)} size={12} />
                  <span className="font-mono text-[10px] tracking-wider text-faint">{avg} / 5</span>
                </span>
              )}
            </div>
            <p className="mt-8 text-base font-light leading-relaxed text-smoke">{product.description}</p>
            <div className="mt-8 flex flex-col gap-3 border-t border-white/10 pt-6 font-mono text-xs tracking-wider text-smoke">
              <div className="flex justify-between"><span className="text-faint">MATERIALS</span><span className="text-right">{product.materials || "—"}</span></div>
              <div className="flex justify-between"><span className="text-faint">AVAILABILITY</span><span>{product.stock > 0 ? `${product.stock} in the atelier` : "Sold out"}</span></div>
              <div className="flex justify-between"><span className="text-faint">SHIPPING</span><span>Worldwide, insured, 5–9 days</span></div>
              <div className="flex justify-between"><span className="text-faint">RETURNS</span><span>30 days, untouched</span></div>
            </div>
            <div className="mt-10 flex items-stretch gap-4">
              <div className="flex items-center border border-white/15">
                <button onClick={() => setQty(Math.max(1, qty - 1))} data-testid="qty-decrease" aria-label="Decrease quantity" className="px-4 py-3 text-smoke hover:text-bone"><Minus size={14} /></button>
                <span className="w-10 text-center font-mono text-sm" data-testid="qty-value">{qty}</span>
                <button onClick={() => setQty(Math.min(product.stock, qty + 1))} data-testid="qty-increase" aria-label="Increase quantity" className="px-4 py-3 text-smoke hover:text-bone"><Plus size={14} /></button>
              </div>
              <motion.button
                whileTap={{ scale: 0.96 }}
                onClick={() => { add(product, qty); toast.success(`${product.name} × ${qty} added to your collection`); }}
                disabled={product.stock <= 0}
                data-testid="product-add-to-cart"
                className="flex-1 bg-vermilion font-mono text-xs uppercase tracking-[0.3em] text-obsidian transition-colors hover:bg-vermilion-dark disabled:opacity-30"
              >
                Add to Cart
              </motion.button>
            </div>
          </Reveal>
        </div>
      </div>

      <div className="mt-24 grid gap-10 border-t border-white/10 pt-16 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <Reveal>
            <p className="eyebrow-label mb-8">Words from Keepers</p>
            {reviews.length === 0 ? (
              <p className="font-display text-2xl italic text-faint">No words yet. Be the first keeper to write.</p>
            ) : (
              <div className="flex flex-col gap-6">
                {reviews.map((r) => (
                  <div key={r.id} className="border border-white/10 bg-onyx p-6" data-testid={`review-${r.id}`}>
                    <div className="flex items-center justify-between">
                      <Stars rating={r.rating} size={12} />
                      <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-brass">Verified Keeper</span>
                    </div>
                    <p className="mt-4 font-display text-lg font-light italic leading-relaxed text-bone">“{r.text}”</p>
                    <p className="mt-4 font-mono text-xs uppercase tracking-[0.25em] text-smoke">{r.name}</p>
                  </div>
                ))}
              </div>
            )}
          </Reveal>
        </div>
        <div className="lg:col-span-4 lg:col-start-9">
          <Reveal delay={0.1}>
            <ReviewForm productId={id} onDone={loadReviews} />
          </Reveal>
        </div>
      </div>
    </div>
  );
}
